import React, { useMemo, useState, useRef, useEffect, Suspense, lazy } from "react";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ListItemText,
  Checkbox,
  OutlinedInput,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TableChartIcon from "@mui/icons-material/TableChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import {
  Chart as ChartJS,
  ScatterController,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Scatter } from "react-chartjs-2";
import { fundService } from "../../../services/fundService";
import { formatProductType } from "../../../utils/enumFormatter";
import { ProductFundYieldsChart } from "./ProductFundYieldsChart";
import { colorHandlerCode, colorByFundId } from "../../../utils/colorEnumFormatter";
import { getHandlerCodeList, formatHandlerCode } from "../../../utils/namesFormater";
import { selectedFundOutlinePlugin, getHighlightedZeroLineGridConfig, stockExposureBandsPlugin } from "../../../shared/UIParts/chartUtils";
import { ProductType } from "../../../interfaces/DTO/mislaka_xml_models";
import { allExposureRanges, allPercentileBuckets, exposureRangeColors, getStockExposureRange, getForeignExposureRange, getCurrencyExposureRange, getPercentileBucket, percentileBucketColors, percentileBucketLabels, formatFundFieldLabel, getSubspecializationColor, fundClassificationColors, stockExposureRanges, stockExposureColors, foreignExposureRanges, foreignExposureColors, currencyExposureRanges, currencyExposureColors, rankingStockExposureBuckets, rankingStockExposureColors, rankingStockExposureLabels } from "../../../utils/fundsFormatter";
import FundChipSelector from "../../../shared/Autocompletes/FundChipSelector";
import { GemelDataFilteredView } from "../../GemelData/types";
import { ActiveFiltersChips } from "../../GemelData/filterLists";
import FundAccordionList from "./FundAccordionList";
import { FundDataBasic } from "../../../interfaces/DTO/FundDataBasic";

ChartJS.register(ScatterController, LinearScale, PointElement, Tooltip, Legend, selectedFundOutlinePlugin, stockExposureBandsPlugin);

// Lazy load the table component
const FundsDataTable = lazy(() => import("./FundsDataTable"));

export interface HighlightedFund {
  fundId: number;
  amount?: number;
}

interface ScatterFundYieldChartProps {
  productType: number;
  filters?: GemelDataFilteredView;
  onFiltersChange?: (filters: GemelDataFilteredView) => void;
  highlightedFunds?: HighlightedFund[];
  selectedFundIds?: number[];
  onSelectedFundsChange?: (fundIds: number[]) => void;
  yieldField?:
    | "avg_annual_yield_trailing_3yrs"
    | "avg_annual_yield_trailing_5yrs"
    | "cumulative_12m_yield"
    | "year_to_date_yield"
    | "cumulative_6m_yield"
    | "yield_trailing_3_yrs"
    | "yield_trailing_5_yrs";
  onYieldFieldChange?: (field: string) => void;
  autoSelectHighlighted?: boolean;
  onFundSelected?: (fund: FundDataBasic) => void;
  editMode?: boolean;
  includeIRA?: boolean;
  /** Sets the "stock-exposure backgrounds" button in the chart title (it stays toggleable); follows prop changes */
  showStockExposureBands?: boolean;
  /** Where to place the fund accordion lists relative to the scatter chart. Defaults to "below". */
  fundListPosition?: "below" | "right";
  /** When set (and on every change of its value), scrolls the nearest scrollable container so the yield-period buttons sit at its top edge */
  scrollToYieldButtonsKey?: string | number;
}

const ScatterFundYieldChart: React.FC<ScatterFundYieldChartProps> = ({
  productType,
  filters,
  onFiltersChange,
  highlightedFunds = [],
  selectedFundIds,
  onSelectedFundsChange,
  yieldField,
  onYieldFieldChange,
  autoSelectHighlighted = false,
  onFundSelected,
  editMode = false,
  includeIRA,
  showStockExposureBands: showStockExposureBandsProp,
  fundListPosition = "below",
  scrollToYieldButtonsKey,
}) => {
  const useFilters = !!filters;

  // Scrolls only the nearest scrollable ancestor (not the page) so the yield buttons are at its top edge
  const yieldButtonsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scrollToYieldButtonsKey === undefined) return;
    const frame = requestAnimationFrame(() => {
      const target = yieldButtonsRef.current;
      let container = target?.parentElement ?? null;
      while (container) {
        const { overflowY } = getComputedStyle(container);
        if ((overflowY === "auto" || overflowY === "scroll") && container.scrollHeight > container.clientHeight) break;
        container = container.parentElement;
      }
      if (!target || !container) return;
      const offset = target.getBoundingClientRect().top - container.getBoundingClientRect().top;
      container.scrollTo({ top: container.scrollTop + offset, behavior: "smooth" });
    });
    return () => cancelAnimationFrame(frame);
  }, [scrollToYieldButtonsKey]);

  // מערך של fund IDs בלבד (לשימוש בקוד הקיים)
  const highlightedFundIds = useMemo(
    () => highlightedFunds.map(f => f.fundId),
    [highlightedFunds]
  );

  // מפה של fundId -> amount
  const fundAmountMap = useMemo(() => {
    const map = new Map<number, number>();
    highlightedFunds.forEach(fund => {
      if (fund.amount !== undefined) {
        map.set(fund.fundId, fund.amount);
      }
    });
    return map;
  }, [highlightedFunds]);

  // פונקציה למחיקת פילטר מסוים
  const handleRemoveFilter = (filterType: 'handler' | 'subSpec' | 'stock' | 'foreign' | 'currency', value: string) => {
    if (!filters || !onFiltersChange) return;

    const updatedFilters = { ...filters };
    switch (filterType) {
      case 'handler':
        updatedFilters.handlers = filters.handlers.filter(h => h !== value);
        break;
      case 'subSpec':
        updatedFilters.subSpecializations = filters.subSpecializations.filter(s => s !== value);
        break;
      case 'stock':
        updatedFilters.stockExposureRanges = filters.stockExposureRanges.filter(r => r !== value);
        break;
      case 'foreign':
        updatedFilters.foreignExposureRanges = filters.foreignExposureRanges.filter(r => r !== value);
        break;
      case 'currency':
        updatedFilters.currencyExposureRanges = filters.currencyExposureRanges.filter(r => r !== value);
        break;
    }
    onFiltersChange(updatedFilters);
  };

  // State פנימי כ-fallback אם לא מעבירים props
  const [internalSelectedFundIds, setInternalSelectedFundIds] = useState<number[]>(
    autoSelectHighlighted ? highlightedFunds.map(f => f.fundId) : []
  );
  const [internalYieldField, setInternalYieldField] = useState<
    | "avg_annual_yield_trailing_3yrs"
    | "avg_annual_yield_trailing_5yrs"
    | "cumulative_12m_yield"
    | "cumulative_6m_yield"
    | "year_to_date_yield"
    | "yield_trailing_3_yrs"
    | "yield_trailing_5_yrs"
  >("cumulative_12m_yield");

  // אם filters.yAxisField קיים, השתמש בו, אחרת fallback ל-yieldField או state פנימי
  const effectiveYieldField =
    (filters?.yAxisField as typeof internalYieldField) ||
    yieldField ||
    internalYieldField;

  // State לגודל ועיצוב
  const [pointSize, setPointSize] = useState<'normal' | 'small' | 'highlight_focus'>('normal');
  const [colorMode, setColorMode] = useState<'default' | 'subspecialization' | 'managing_corporation' | 'fund_classification' | 'stock_exposure' | 'foreign_exposure' | 'currency_exposure' | 'percentile_6m_yield'>(
    filters?.displayPreferences?.colorBy || 'default'
  );
  const [selectedSubSpecializations, setSelectedSubSpecializations] = useState<string[]>([]);
  const [selectedManagingCorporations, setSelectedManagingCorporations] = useState<string[]>([]);
  const [selectedStockExposureRanges, setSelectedStockExposureRanges] = useState<string[]>([]);
  const [selectedForeignExposureRanges, setSelectedForeignExposureRanges] = useState<string[]>([]);
  const [selectedCurrencyExposureRanges, setSelectedCurrencyExposureRanges] = useState<string[]>([]);
  const [selectedPercentileBuckets, setSelectedPercentileBuckets] = useState<string[]>([]);
  
  // State עבור ציר X
  const [xAxisField, setXAxisField] = useState<'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent'>('stock_market_percent');

  // State להצגת שטחים אנכיים המסמנים buckets של חשיפה למניות (רלוונטי רק כאשר ציר X = חשיפה למניות)
  const [showStockExposureBands, setShowStockExposureBands] = useState(showStockExposureBandsProp ?? false);
  useEffect(() => {
    if (showStockExposureBandsProp !== undefined) setShowStockExposureBands(showStockExposureBandsProp);
  }, [showStockExposureBandsProp]);

  // חדש: State עבור קרנות שנוספו דרך החיפוש החדש
  const [additionalFundIds, setAdditionalFundIds] = useState<number[]>([]);

  // State עבור שליטה בתצוגת קבוצות קרנות
  const [displayOptions, setDisplayOptions] = useState({
    showFiltered: false,       // הצג קרנות מסוננות
    showAllPopulation: true,   // הצג כלל האוכלוסייה - ברירת מחדל
    showHighlighted: true,     // הצג קרנות בתיק (highlighted)
    showSelected: true,        // הצג קרנות נבחרות
  });

  // Ref לתרשים כדי לשלוט באנימציה
  const chartRef = useRef<any>(null);

  // State לבחירת fundId להדגשה
  const [selectedScatterFundId, setSelectedScatterFundId] = useState<number | null>(null);

  // State עבור toggle בין chart לטבלה
  const [viewMode, setViewMode] = useState<"chart" | "table">("chart");

  // סנכרון colorMode עם filters.displayPreferences.colorBy
  useEffect(() => {
    if (filters?.displayPreferences?.colorBy) {
      setColorMode(filters.displayPreferences.colorBy);
    }
  }, [filters?.displayPreferences?.colorBy]);

  // סנכרון internalYieldField עם filters.yAxisField או yieldField prop
  useEffect(() => {
    if (filters?.yAxisField) {
      setInternalYieldField(filters.yAxisField as typeof internalYieldField);
    } else if (yieldField) {
      setInternalYieldField(yieldField);
    }
  }, [filters?.yAxisField, yieldField]);

  // כאשר מוסיפים פילטר חדש, הפעל אוטומטית את תצוגת הקרנות המסוננות
  useEffect(() => {
    if (!useFilters || !filters) return;

    const hasActiveFilters =
      (filters.handlers && filters.handlers.length > 0) ||
      (filters.subSpecializations && filters.subSpecializations.length > 0) ||
      (filters.stockExposureRanges && filters.stockExposureRanges.length > 0) ||
      (filters.foreignExposureRanges && filters.foreignExposureRanges.length > 0) ||
      (filters.currencyExposureRanges && filters.currencyExposureRanges.length > 0);

    if (hasActiveFilters && !displayOptions.showFiltered) {
      setDisplayOptions(prev => ({ ...prev, showFiltered: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    useFilters,
    filters?.handlers,
    filters?.subSpecializations,
    filters?.stockExposureRanges,
    filters?.foreignExposureRanges,
    filters?.currencyExposureRanges,
  ]);

  // השתמש ב-props אם קיימים, אחרת ב-state פנימי
  const actualSelectedFundIds = selectedFundIds ?? internalSelectedFundIds;
  const actualOnSelectedFundsChange = onSelectedFundsChange ?? setInternalSelectedFundIds;

  // טיפול ב-yieldField - אם יש filters.yAxisField, השתמש בו, אחרת ב-yieldField או state פנימי
  const actualOnYieldFieldChange = (newField: string) => {
    if (onFiltersChange && filters) {
      // אם משתמשים ב-filters, עדכן גם את yAxisField
      const updatedFilters = { ...filters, yAxisField: newField as any };
      onFiltersChange(updatedFilters);
    }
    if (onYieldFieldChange) {
      // לתאימות לאחור - קרא גם ל-callback הישן
      onYieldFieldChange(newField);
    } else {
      // fallback ל-state פנימי
      setInternalYieldField(newField as typeof internalYieldField);
    }
  };

  // שלב קרנות מ-highlighted וקרנות שנוספו דרך החיפוש
  const combinedHighlightedFundIds = [...highlightedFundIds, ...additionalFundIds];

  // חישוב הקרנות המוצגות בהתאם לאפשרויות התצוגה
  const { funds, filteredFundIds, allFundsCount } = useMemo(() => {
    const allFunds = fundService.getFundsFromCacheByCriteria({
      productType,
      forGeneralPublic: filters?.forGeneralPublic,
      includeIRA: filters?.includeIRA ?? includeIRA ?? false,
    });
    const allFundsCount = allFunds.length;

    // אם אין filters, הצג הכל
    if (!useFilters || !filters) {
      return {
        funds: allFunds,
        filteredFundIds: allFunds.map(f => f.fund_id),
        allFundsCount,
      };
    }

    const filteredIds = fundService.getFilteredFundIds(filters);
    const highlightedIds = highlightedFunds.map(f => f.fundId);
    const selectedIds = actualSelectedFundIds || [];

    // קבע אילו קרנות להציג בהתאם לאפשרויות
    let fundsToShow = new Set<number>();

    // אם showAllPopulation מסומן, הוסף את כל הקרנות
    // אחרת, הוסף רק את הקבוצות המסומנות
    if (displayOptions.showAllPopulation) {
      allFunds.forEach(f => fundsToShow.add(f.fund_id));
    } else {
      // הוסף רק קרנות מהקבוצות המסומנות (ולא כלל האוכלוסייה)
      if (displayOptions.showFiltered) {
        filteredIds.forEach(id => fundsToShow.add(id));
      }

      if (displayOptions.showHighlighted) {
        highlightedIds.forEach((id: number) => fundsToShow.add(id));
      }

      if (displayOptions.showSelected) {
        selectedIds.forEach((id: number) => fundsToShow.add(id));
      }
    }

    // המר ל-array של קרנות
    const fundsToDisplay = allFunds.filter(fund => fundsToShow.has(fund.fund_id));

    return {
      funds: fundsToDisplay,
      filteredFundIds: filteredIds,
      allFundsCount,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    useFilters,
    productType,
    displayOptions.showFiltered,
    displayOptions.showAllPopulation,
    displayOptions.showHighlighted,
    displayOptions.showSelected,
    JSON.stringify(filters?.handlers || []),
    JSON.stringify(filters?.subSpecializations || []),
    JSON.stringify(filters?.stockExposureRanges || []),
    JSON.stringify(filters?.foreignExposureRanges || []),
    JSON.stringify(filters?.currencyExposureRanges || []),
    JSON.stringify(highlightedFunds || []),
    JSON.stringify(actualSelectedFundIds || []),
    filters?.forGeneralPublic,
  ]);

  // מספר יצרנים ייחודיים מבין כלל הקרנות המוצגות כרגע בפועל בגרף (funds - העיגולים שרואים)
  const visibleManufacturersCount = useMemo(() => {
    return new Set(
      funds.map(f => f.managing_corporation_legal_id).filter(id => id !== null && id !== undefined)
    ).size;
  }, [funds]);

  // מספר יצרנים ייחודיים מבין הקרנות שעונות לפילטר בפועל (מתוך אלו המוצגות בגרף)
  const filteredManufacturersCount = useMemo(() => {
    const filteredIdsSet = new Set(filteredFundIds);
    return new Set(
      funds
        .filter(f => filteredIdsSet.has(f.fund_id))
        .map(f => f.managing_corporation_legal_id)
        .filter(id => id !== null && id !== undefined)
    ).size;
  }, [funds, filteredFundIds]);

  // פונקציה לקבלת האופציות הזמינות לצביעה
  const getAvailableColorModes = () => {
    const baseModes = [
      { value: 'default', label: 'צבע רגיל' },
      { value: 'subspecialization', label: 'לפי התמחות' },
      { value: 'managing_corporation', label: 'לפי יצרן' },
      { value: 'percentile_6m_yield', label: 'לפי דירוג תשואה 6M' },
    ];

    // הוסף אופציית סוג פנסיה רק אם זה פנסיה
    if (productType === ProductType.PENSION_FUND) {
      baseModes.push({ value: 'fund_classification', label: 'לפי סוג פנסיה' });
    }

    // הוסף את כל שלושת אופציות החשיפה, תמיד
    baseModes.push(
      { value: 'stock_exposure', label: 'לפי חשיפה למניות' },
      { value: 'foreign_exposure', label: 'לפי חשיפה לחו"ל' },
      { value: 'currency_exposure', label: 'לפי חשיפה למט"ח ' }
    );

    return baseModes;
  };

  // פונקציה לקבלת שם החשיפה לפי מוד הצביעה
  const getExposureFieldByColorMode = (mode: string): string => {
    switch (mode) {
      case 'stock_exposure': return 'stock_market_percent';
      case 'foreign_exposure': return 'foreign_exposure_percent';
      case 'currency_exposure': return 'foreign_currency_exposure_percent';
      default: return 'stock_market_percent';
    }
  };

  // פונקציה לקבלת הטווחים הנבחרים לפי מוד הצביעה
  const getSelectedRangesByColorMode = (mode: string): string[] => {
    if (useFilters && filters) {
      switch (mode) {
        case 'stock_exposure': return filters.stockExposureRanges || [];
        case 'foreign_exposure': return filters.foreignExposureRanges || [];
        case 'currency_exposure': return filters.currencyExposureRanges || [];
        default: return [];
      }
    }

    switch (mode) {
      case 'stock_exposure': return selectedStockExposureRanges;
      case 'foreign_exposure': return selectedForeignExposureRanges;
      case 'currency_exposure': return selectedCurrencyExposureRanges;
      default: return [];
    }
  };

  // פונקציה לעדכון הטווחים הנבחרים לפי מוד הצביעה
  const setSelectedRangesByColorMode = (mode: string, ranges: string[]) => {
    if (useFilters && filters && onFiltersChange) {
      const updatedFilters = { ...filters };
      switch (mode) {
        case 'stock_exposure':
          updatedFilters.stockExposureRanges = ranges;
          break;
        case 'foreign_exposure':
          updatedFilters.foreignExposureRanges = ranges;
          break;
        case 'currency_exposure':
          updatedFilters.currencyExposureRanges = ranges;
          break;
      }
      onFiltersChange(updatedFilters);
      return;
    }

    switch (mode) {
      case 'stock_exposure': setSelectedStockExposureRanges(ranges); break;
      case 'foreign_exposure': setSelectedForeignExposureRanges(ranges); break;
      case 'currency_exposure': setSelectedCurrencyExposureRanges(ranges); break;
    }
  };

  // פונקציה לקבלת כותרת החשיפה לפי מוד הצביעה
  const getExposureTitleByColorMode = (mode: string): string => {
    switch (mode) {
      case 'stock_exposure': return 'חשיפה למניות';
      case 'foreign_exposure': return 'חשיפה לחו"ל';
      case 'currency_exposure': return 'חשיפה למט"ח ';
      default: return 'חשיפה';
    }
  };

  // פונקציה לקבלת טווחי החשיפה לפי מוד הצביעה
  const getExposureRangesByColorMode = (mode: string): string[] => {
    switch (mode) {
      case 'stock_exposure': return stockExposureRanges;
      case 'foreign_exposure': return foreignExposureRanges;
      case 'currency_exposure': return currencyExposureRanges;
      default: return allExposureRanges;
    }
  };

  // פונקציה לקבלת צבעי החשיפה לפי מוד הצביעה
  const getExposureColorsByColorMode = (mode: string): Record<string, string> => {
    switch (mode) {
      case 'stock_exposure': return stockExposureColors;
      case 'foreign_exposure': return foreignExposureColors;
      case 'currency_exposure': return currencyExposureColors;
      default: return exposureRangeColors;
    }
  };

  // מציאת כל ערכי sub_specialization הייחודיים
  const allSubSpecializations = useMemo(() => {
    const uniqueValues = [...new Set(funds.map(f => f.sub_specialization || '-'))];
    return uniqueValues.sort();
  }, [funds]);

  // מציאת כל יצרני הקרנות הכלליים
  const generalManagingCorporations = useMemo(() => {
    const handlerList = getHandlerCodeList();
    return handlerList.filter(handler => handler.isGeneral);
  }, []);

  // מיפוי צבעים לsub_specialization - שימוש בפונקציה שמבטיחה קונסיסטנטיות
  const subspecializationColorMap = useMemo(() => {
    const colorMap: { [key: string]: string } = {};
    allSubSpecializations.forEach((spec) => {
      colorMap[spec] = getSubspecializationColor(spec);
    });
    return colorMap;
  }, [allSubSpecializations]);

  // פונקציה להחזרת כותרת ציר X
  const getXAxisTitle = () => {
    switch (xAxisField) {
      case "stock_market_percent":
        return "% חשיפה למניות";
      case "foreign_exposure_percent":
        return "% חשיפה לחו\"ל";
      case "foreign_currency_exposure_percent":
        return "% חשיפה למט\"ח ";
      default:
        return "% חשיפה למניות";
    }
  };

  // עדכון colorMode כאשר ציר ה-X משתנה
  const handleXAxisChange = (newXAxisField: typeof xAxisField) => {
    setXAxisField(newXAxisField);
    
    // אם מוד הצביעה הנוכחי הוא exposure והוא לא זמין יותר, החזר לdefault
    const isExposureMode = ['stock_exposure', 'foreign_exposure', 'currency_exposure'].includes(colorMode);
    if (isExposureMode) {
      const availableModes = getAvailableColorModes();
      const isCurrentModeAvailable = availableModes.some(mode => mode.value === colorMode);
      if (!isCurrentModeAvailable) {
        setColorMode('default');
      }
    }
  };

  // טיפול בקרנות שנוספו דרך FundChipSelector
  const handleAdditionalFundsChange = (newAdditionalFundIds: number[]) => {
    const oldAdditionalFundIds = additionalFundIds;
    
    // עדכן את state של הקרנות הנוספות
    setAdditionalFundIds(newAdditionalFundIds);
    
    // מצא קרנות שהוסרו
    const removedFundIds = oldAdditionalFundIds.filter(id => !newAdditionalFundIds.includes(id));
    
    // מצא קרנות שנוספו
    const addedFundIds = newAdditionalFundIds.filter(id => !oldAdditionalFundIds.includes(id));
    
    // עדכן את הרשימה של הקרנות הנבחרות
    let newSelectedIds = [...actualSelectedFundIds];
    
    // הסר קרנות שהוסרו
    newSelectedIds = newSelectedIds.filter(id => !removedFundIds.includes(id));
    
    // הוסף קרנות חדשות
    addedFundIds.forEach(id => {
      if (!newSelectedIds.includes(id)) {
        newSelectedIds.push(id);
      }
    });
    
    actualOnSelectedFundsChange(newSelectedIds);
  };

  const chartData = useMemo(() => {
    const allData = funds
      .filter(
        (f) =>
          f[effectiveYieldField] !== undefined &&
          f[effectiveYieldField] !== null &&
          f[xAxisField] !== undefined &&
          f[xAxisField] !== null
      )
      .map((fund) => {
        // התחשב ב-displayOptions בעת קביעת isSelected/isHighlighted
        const isSelected = displayOptions.showSelected && actualSelectedFundIds.includes(fund.fund_id);
        const isHighlighted = displayOptions.showHighlighted && combinedHighlightedFundIds.includes(fund.fund_id);
        const isImportant = isSelected || isHighlighted;
        const isInFilteredPopulation = filteredFundIds.includes(fund.fund_id);

        let backgroundColor: string;

        // אם הפילטר מוצג והקרן לא בפילטר והיא לא selected/highlighted, צבע אפור
        // אבל אם הפילטר לא מוצג (showFiltered=false), אל תצבע באפור
        if (useFilters && displayOptions.showFiltered && !isInFilteredPopulation && !isImportant) {
          backgroundColor = 'rgba(180, 180, 180, 0.3)';
        } else if (colorMode === 'subspecialization') {
          const subspecialization = fund.sub_specialization || '-';
          const shouldShow = selectedSubSpecializations.length === 0 ||
                           selectedSubSpecializations.includes(subspecialization);

          if (pointSize === 'highlight_focus' && !isImportant) {
            backgroundColor = shouldShow ? 'rgba(240, 240, 240, 0.8)' : 'rgba(200,200,200,0.3)';
          } else {
            backgroundColor = shouldShow ? subspecializationColorMap[subspecialization] : 'rgba(200,200,200,0.3)';
          }
        } else if (colorMode === 'managing_corporation') {
          const managingCorp = fund.managing_corporation_legal_id ? String(fund.managing_corporation_legal_id) : '-';
          const shouldShow = selectedManagingCorporations.length === 0 ||
                           selectedManagingCorporations.includes(managingCorp);

          if (pointSize === 'highlight_focus' && !isImportant) {
            backgroundColor = shouldShow ? 'rgba(240, 240, 240, 0.8)' : 'rgba(200,200,200,0.3)';
          } else {
            backgroundColor = shouldShow && fund.managing_corporation_legal_id
              ? colorHandlerCode(fund.managing_corporation_legal_id)
              : shouldShow
              ? 'rgb(170,170,170)' // צבע אטום ובהיר
              : 'rgba(200,200,200,0.3)';
          }
        } else if (colorMode === 'fund_classification') {
          const classification = fund.fund_classification || "-";

          if (pointSize === 'highlight_focus' && !isImportant) {
            backgroundColor = 'rgba(240, 240, 240, 0.8)';
          } else {
            backgroundColor = fundClassificationColors[classification] || "#BDBDBD";
          }
        } else if (colorMode === 'percentile_6m_yield') {
          const percentileValue = fund.percentile_6m_yield || 0;
          const percentileBucket = getPercentileBucket(percentileValue);
          const shouldShow = selectedPercentileBuckets.length === 0 || selectedPercentileBuckets.includes(percentileBucket);

          if (pointSize === 'highlight_focus' && !isImportant) {
            backgroundColor = shouldShow ? 'rgba(240, 240, 240, 0.8)' : 'rgba(200,200,200,0.3)';
          } else {
            backgroundColor = shouldShow ? percentileBucketColors[percentileBucket] : 'rgba(200,200,200,0.3)';
          }
        } else if (['stock_exposure', 'foreign_exposure', 'currency_exposure'].includes(colorMode)) {
          const exposureField = getExposureFieldByColorMode(colorMode);
          const exposureValue = fund[exposureField as keyof typeof fund] as number || 0;
          // קבל את הפונקציה המתאימה לטווח החשיפה
          const getExposureRangeFunc = colorMode === 'stock_exposure' ? getStockExposureRange :
                                       colorMode === 'foreign_exposure' ? getForeignExposureRange :
                                       getCurrencyExposureRange;
          const exposureRange = getExposureRangeFunc(exposureValue);
          const selectedRanges = getSelectedRangesByColorMode(colorMode);
          const shouldShow = selectedRanges.length === 0 || selectedRanges.includes(exposureRange);
          const currentExposureColors = getExposureColorsByColorMode(colorMode);

          if (pointSize === 'highlight_focus' && !isImportant) {
            backgroundColor = shouldShow ? 'rgba(240, 240, 240, 0.8)' : 'rgba(200,200,200,0.3)';
          } else {
            backgroundColor = shouldShow ? currentExposureColors[exposureRange] : 'rgba(200,200,200,0.3)';
          }
        } else {
          if (pointSize === 'highlight_focus' && !isImportant) {
            backgroundColor = 'rgba(240, 240, 240, 0.8)';
          } else {
            // צבע הנקודה עצמה - רק קרנות selected מקבלות צבע מיוחד
            backgroundColor = isSelected
              ? colorByFundId(fund.fund_id)
              : "#8FD5D5"; // כל השאר (כולל highlighted) בצבע רגיל
          }
        }

        // הגדרת border לפי מצב הבחירה
        let borderColor: string;
        let borderWidth: number = 1;

        const isFundSelected = actualSelectedFundIds.includes(fund.fund_id);
        if (isFundSelected) {
          borderColor = 'rgba(0, 0, 0, 0.2)';
          borderWidth = 1;
        } else if (pointSize === 'highlight_focus' && !isImportant) {
          borderColor = 'rgba(180, 180, 180, 0.8)';
          borderWidth = 1;
        } else if (colorMode === 'managing_corporation') {
          const managingCorp = fund.managing_corporation_legal_id ? String(fund.managing_corporation_legal_id) : '-';
          const isSelectedCorp = selectedManagingCorporations.length > 0 && selectedManagingCorporations.includes(managingCorp);

          if (isSelectedCorp && fund.managing_corporation_legal_id) {
            const baseColor = colorHandlerCode(fund.managing_corporation_legal_id);
            borderColor = baseColor.replace('rgb', 'rgba').replace(')', ', 0.8)');
            borderWidth = 2;
          } else {
            borderColor = 'rgba(0,0,0,0.2)';
          }
        } else {
          borderColor = 'rgba(0,0,0,0.2)';
        }

        const additionalData: any = {
          x: fund[xAxisField]!,
          y: fund[effectiveYieldField]!,
          label: fund.fund_name || "ללא שם",
          fund_id: fund.fund_id,
          subspecialization: fund.sub_specialization || '-',
          fund_classification: fund.fund_classification || "-",
          managing_corporation_legal_id: fund.managing_corporation_legal_id ? String(fund.managing_corporation_legal_id) : undefined,
          backgroundColor,
          borderColor,
          borderWidth,
          isImportant,
          xValue: fund[xAxisField]!,
          percentile_6m_yield: fund.percentile_6m_yield || 0,
          isSelected: isFundSelected, // סימון שהקרן נבחרת למעגל סגול
          isHighlighted: isHighlighted, // סימון שהקרן בתיק למעגל כתום
        };

        additionalData.stock_exposure_range = getStockExposureRange(fund.stock_market_percent || 0);
        additionalData.foreign_exposure_range = getForeignExposureRange(fund.foreign_exposure_percent || 0);
        additionalData.currency_exposure_range = getCurrencyExposureRange(fund.foreign_currency_exposure_percent || 0);
        additionalData.percentile_bucket = getPercentileBucket(fund.percentile_6m_yield || 0);
        additionalData.fundId = fund.fund_id; // הוסף fundId לשימוש בפלאגין

        return additionalData;
      });

    // פיצול הנתונים ל-2 datasets: רגיל ו-highlighted
    const normalData = allData.filter(d => !d.isImportant);
    const highlightedData = allData.filter(d => d.isImportant);

    let pointRadius: number | ((ctx: any) => number);
    let pointHoverRadius: number | ((ctx: any) => number);

    if (pointSize === 'highlight_focus') {
      pointRadius = (ctx: any) => ctx.raw.isImportant ? 5 : 2;
      pointHoverRadius = (ctx: any) => ctx.raw.isImportant ? 6 : 3;
    } else {
      pointRadius = pointSize === 'small' ? 3 : 5;
      pointHoverRadius = pointSize === 'small' ? 4 : 6;
    }

    // יצירת datasets נפרדים - הרגילים קודם, ה-highlighted אחריהם
    const datasets = [];

    // Dataset לנקודות רגילות
    if (normalData.length > 0) {
      datasets.push({
        label: "",
        data: normalData,
        backgroundColor: (ctx: any) => ctx.raw.backgroundColor,
        borderColor: (ctx: any) => ctx.raw.borderColor,
        borderWidth: (ctx: any) => ctx.raw.borderWidth,
        pointRadius,
        pointHoverRadius,
        order: 2, // נקודות רגילות יוצגו תחת ה-highlighted
      });
    }

    // Dataset לנקודות highlighted - יבוא אחרון ויוצג מעל
    if (highlightedData.length > 0) {
      datasets.push({
        label: "",
        data: highlightedData,
        backgroundColor: (ctx: any) => ctx.raw.backgroundColor,
        borderColor: (ctx: any) => ctx.raw.borderColor,
        borderWidth: (ctx: any) => ctx.raw.borderWidth,
        pointRadius,
        pointHoverRadius,
        order: 1, // נקודות highlighted יוצגו מעל הרגילות
      });
    }

    return {
      datasets,
    };
  }, [
    funds,
    effectiveYieldField,
    actualSelectedFundIds,
    combinedHighlightedFundIds,
    colorMode,
    selectedSubSpecializations,
    selectedManagingCorporations,
    selectedStockExposureRanges,
    selectedForeignExposureRanges,
    selectedCurrencyExposureRanges,
    selectedPercentileBuckets,
    subspecializationColorMap,
    pointSize,
    xAxisField,
    displayOptions.showSelected,
    displayOptions.showHighlighted,
    filteredFundIds,
    useFilters,
  ]);

  const getYAxisTitle = () => {
    return formatFundFieldLabel(effectiveYieldField).label;
  };

    const chartOptions = useMemo(() => {
    // בדיקה אם יש ערכים שליליים בציר ה־Y
    const hasNegativeValues =
      chartData.datasets[0]?.data?.some((d: any) => d.y < 0) ?? false;

    return {
      selectedFundId: selectedScatterFundId, // העבר את ה-fundId הנבחר לפלאגין
      showStockExposureBands: showStockExposureBands && xAxisField === 'stock_market_percent',
      stockExposureBandsConfig: {
        buckets: rankingStockExposureBuckets,
        colors: rankingStockExposureColors,
        labels: rankingStockExposureLabels,
      },
      maintainAspectRatio: false,
      responsive: true,
      animation: {
        duration: 750, // אנימציה רגילה לשינויים בשדות תצוגה
      },
      transitions: {
        active: {
          animation: {
            duration: 0, // ביטול אנימציה בעת hover
          }
        }
      },
      scales: {
        x: {
          type: "linear" as const,
          title: {
            display: true,
            text: getXAxisTitle(),
            font: {
              size: 16,
              weight: 'bold' as const,
            },
          },
          min: -2,
          max: 102,
          afterBuildTicks: (axis: any) => {
            // הוספת tick אמיתי בדיוק על 100, כדי שהתווית תופיע במקום הנכון על הציר
            // (102 קיים רק כשוליים חזותיים כדי שעיגולים על 100-101 לא ייחתכו, בדומה ל-min: -2)
            const hasHundred = axis.ticks.some((t: any) => t.value === 100);
            if (!hasHundred) {
              axis.ticks.push({ value: 100 });
              axis.ticks.sort((a: any, b: any) => a.value - b.value);
            }
          },
          ticks: {
            callback: function(value: any) {
              // הסתרת התוויות של -2 ו-102 (שוליים חזותיים בלבד)
              if (value === -2 || value === 102) {
                return '';
              }
              return value;
            },
          },
        },
        y: {
          type: "linear" as const,
          title: {
            display: true,
            text: getYAxisTitle(),
            font: {
              size: 16,
              weight: 'bold' as const,
            },
          },
          beginAtZero: !hasNegativeValues, // מתחיל מ־0 רק אם אין ערכים שליליים
          grid: getHighlightedZeroLineGridConfig(), // תמיד הדגש את קו Y=0
        },
      },
      plugins: {
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const {
                label,
                fund_id,
                y,
                xValue,
                subspecialization,
                managing_corporation_legal_id,
                fund_classification,
                stock_exposure_range,
                foreign_exposure_range,
                currency_exposure_range,
                percentile_6m_yield,
                percentile_bucket,
              } = context.raw;

              const yAxisLabel = formatFundFieldLabel(effectiveYieldField).label;
              const lines = [
                `${label} (${fund_id})`,
                `${yAxisLabel}: ${y.toFixed(2)}%`,
                `${getXAxisTitle()}: ${xValue.toFixed(2)}%`,
              ];

              if (colorMode === "subspecialization") {
                lines.push(`התמחות: ${subspecialization}`);
              } else if (colorMode === "managing_corporation") {
                const formattedHandler = managing_corporation_legal_id
                  ? formatHandlerCode(managing_corporation_legal_id)
                  : "לא זמין";
                lines.push(`יצרן: ${formattedHandler}`);
              } else if (colorMode === "fund_classification") {
                lines.push(`סיווג קרן: ${fund_classification || "-"}`);
              } else if (colorMode === "percentile_6m_yield") {
                lines.push(
                  `דירוג תשואה 6M: ${percentile_6m_yield.toFixed(1)}% (${percentileBucketLabels[percentile_bucket]})`
                );
              } else if (colorMode === "stock_exposure") {
                lines.push(`טווח חשיפה למניות: ${stock_exposure_range}%`);
              } else if (colorMode === "foreign_exposure") {
                lines.push(`טווח חשיפה לחו"ל: ${foreign_exposure_range}%`);
              } else if (colorMode === "currency_exposure") {
                lines.push(`טווח חשיפה למט"ח : ${currency_exposure_range}%`);
              }

              return lines;
            },
          },
          mode: "index" as const,
          intersect: true,
        },
        datalabels: {
          display: false,
        },
        legend: {
          display: false,
        },
      },
      elements: {
        point: {
          pointStyle: "circle",
        },
      },
      onClick: (_event: any, elements: any[]) => {
        if (elements.length > 0) {
          const datasetIndex = elements[0].datasetIndex;
          const index = elements[0].index;
          const fund = chartData.datasets[datasetIndex].data[index] as any;
          const id = fund.fund_id;

          // הדגש את הקרן
          setSelectedScatterFundId(prev => prev === id ? null : id);

          const newSelectedIds = actualSelectedFundIds.includes(id)
            ? actualSelectedFundIds.filter((x) => x !== id)
            : [...actualSelectedFundIds, id];

          // עדכון ללא אנימציה
          if (chartRef.current) {
            chartRef.current.options.animation = { duration: 0 };
          }

          actualOnSelectedFundsChange(newSelectedIds);

          // החזרת האנימציה לאחר העדכון
          setTimeout(() => {
            if (chartRef.current) {
              chartRef.current.options.animation = { duration: 750 };
            }
          }, 0);
        } else {
          // לחיצה על איזור ריק - בטל הדגשה
          setSelectedScatterFundId(null);
        }
      },
    };
  }, [chartData, effectiveYieldField, actualSelectedFundIds, colorMode, xAxisField, selectedScatterFundId, showStockExposureBands]);


  const availableColorModes = getAvailableColorModes();
  const isCurrentModeExposure = ['stock_exposure', 'foreign_exposure', 'currency_exposure'].includes(colorMode);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'relative' }}>
      <Box display="flex" alignItems="center" gap={2}>
        <Typography variant="h6">
          תרשים פיזור – תשואה מול
        </Typography>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select
            value={xAxisField}
            onChange={(e) => handleXAxisChange(e.target.value as typeof xAxisField)}
            variant="standard"
            sx={{ fontSize: '1.25rem', fontWeight: 500 }}
            MenuProps={{ sx: { '& .MuiMenu-paper': { direction: 'rtl' } } }}
          >
            <MenuItem value="stock_market_percent" sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>% חשיפה למניות</MenuItem>
            <MenuItem value="foreign_currency_exposure_percent" sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>% חשיפה למט"ח </MenuItem>
            <MenuItem value="foreign_exposure_percent" sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>% חשיפה לחו"ל</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="h6">
          ({formatProductType(productType)})
        </Typography>
        {xAxisField === 'stock_market_percent' && (
          <IconButton
            size="small"
            onClick={() => setShowStockExposureBands(prev => !prev)}
            title="הצג/הסתר שטחים לפי טווחי חשיפה למניות"
            sx={{
              border: '1.5px solid rgba(0, 0, 0, 0.12)',
              borderRadius: '8px',
              color: showStockExposureBands ? '#1976d2' : 'text.secondary',
              backgroundColor: showStockExposureBands ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
              borderColor: showStockExposureBands ? '#1976d2' : 'rgba(0, 0, 0, 0.12)',
              '&:hover': {
                backgroundColor: showStockExposureBands ? 'rgba(25, 118, 210, 0.16)' : 'rgba(25, 118, 210, 0.04)',
              },
            }}
          >
            <ViewColumnIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* חדש: שימוש ב-FundChipSelector במקום הקוד הישן */}
      <FundChipSelector
        selectedFundIds={additionalFundIds}
        onSelectedFundsChange={handleAdditionalFundsChange}
        productTypeFilter={[productType]}
        label="הוסף קרנות לניתוח"
        placeholder="חפש והוסף קרן נוספת לתרשים..."
        variant="filled"
        size="small"
        showFundInfo={true}
        useCustomColors={true}
      />

      {/* Dropdowns for controls */}
      <Box display="flex" gap={2} justifyContent="space-between" alignItems="center">
        <Box display="flex" gap={2}>
          {/* Point Size Dropdown */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>גודל עיגולים</InputLabel>
            <Select
              value={pointSize}
              onChange={(e) => setPointSize(e.target.value as 'normal' | 'small' | 'highlight_focus')}
              label="גודל עיגולים"
              MenuProps={{ sx: { '& .MuiMenu-paper': { direction: 'rtl' } } }}
          >
            <MenuItem value="normal" sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: 'rgba(75,192,192,0.6)',
                    border: '1px solid rgba(75,192,192,1)',
                  }}
                />
                <span>רגיל</span>
              </Box>
            </MenuItem>
            <MenuItem value="small" sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'rgba(75,192,192,0.6)',
                    border: '1px solid rgba(75,192,192,1)',
                  }}
                />
                <span>קטן</span>
              </Box>
            </MenuItem>
            <MenuItem value="highlight_focus" sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: 'rgba(75,192,192,0.6)',
                      border: '1px solid rgba(75,192,192,1)',
                    }}
                  />
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: 'rgba(240,240,240,0.8)',
                      border: '1px solid rgba(180,180,180,0.8)',
                    }}
                  />
                </Box>
                <span>הדגשת בחורים</span>
              </Box>
            </MenuItem>
          </Select>
        </FormControl>

        {/* Color Mode Dropdown */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>צביעה</InputLabel>
          <Select
            value={colorMode}
            onChange={(e) => {
              const newValue = e.target.value as typeof colorMode;
              setColorMode(newValue);

              // עדכן את DisplayPreferences.colorBy אם יש filters
              if (filters && onFiltersChange) {
                const updatedFilters = {
                  ...filters,
                  displayPreferences: {
                    ...filters.displayPreferences,
                    colorBy: newValue as any,
                  },
                };
                onFiltersChange(updatedFilters);
              }

              if (newValue !== 'subspecialization') {
                setSelectedSubSpecializations([]);
              }
              if (newValue !== 'managing_corporation') {
                setSelectedManagingCorporations([]);
              }
              if (newValue !== 'percentile_6m_yield') {
                setSelectedPercentileBuckets([]);
              }
              if (!['stock_exposure', 'foreign_exposure', 'currency_exposure'].includes(newValue)) {
                setSelectedStockExposureRanges([]);
                setSelectedForeignExposureRanges([]);
                setSelectedCurrencyExposureRanges([]);
              }
            }}
            label="צביעה"
            MenuProps={{ sx: { '& .MuiMenu-paper': { direction: 'rtl' } } }}
          >
            {availableColorModes.map((mode) => (
              <MenuItem key={mode.value} value={mode.value} sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>
                {mode.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Sub-Specialization Multi-Select */}
        {colorMode === 'subspecialization' && (
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>התמחויות</InputLabel>
            <Select
              multiple
              value={selectedSubSpecializations}
              onChange={(e) => {
                const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                setSelectedSubSpecializations(value);
              }}
              input={<OutlinedInput label="התמחויות" />}
              renderValue={(selected) =>
                selected.length === 0 ? 'הכל' : `${selected.length} נבחרו`
              }
              MenuProps={{ sx: { '& .MuiMenu-paper': { direction: 'rtl' } } }}
            >
              {allSubSpecializations.map((spec) => (
                <MenuItem key={spec} value={spec} sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>
                  <Checkbox checked={selectedSubSpecializations.indexOf(spec) > -1} />
                  <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: subspecializationColorMap[spec],
                      }}
                    />
                    <ListItemText primary={spec} />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Managing Corporation Multi-Select */}
        {colorMode === 'managing_corporation' && (
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>יצרנים</InputLabel>
            <Select
              multiple
              value={selectedManagingCorporations}
              onChange={(e) => {
                const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                setSelectedManagingCorporations(value);
              }}
              input={<OutlinedInput label="יצרנים" />}
              renderValue={(selected) =>
                selected.length === 0 ? 'הכל' : `${selected.length} נבחרו`
              }
              MenuProps={{ sx: { '& .MuiMenu-paper': { direction: 'rtl' } } }}
            >
              {generalManagingCorporations.map((handler) => (
                <MenuItem key={handler.code} value={handler.code} sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>
                  <Checkbox checked={selectedManagingCorporations.indexOf(handler.code) > -1} />
                  <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: colorHandlerCode(handler.code),
                      }}
                    />
                    <ListItemText primary={handler.name} />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Percentile Buckets Multi-Select */}
        {colorMode === 'percentile_6m_yield' && (
          <FormControl size="small" sx={{ minWidth: 300 }}>
            <InputLabel>דירוג תשואה 6M</InputLabel>
            <Select
              multiple
              value={selectedPercentileBuckets}
              onChange={(e) => {
                const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                setSelectedPercentileBuckets(value);
              }}
              input={<OutlinedInput label="דירוג תשואה 6M" />}
              renderValue={(selected) =>
                selected.length === 0 ? 'הכל' : `${selected.length} נבחרו`
              }
              MenuProps={{ sx: { '& .MuiMenu-paper': { direction: 'rtl' } } }}
            >
              {allPercentileBuckets.map((bucket) => (
                <MenuItem key={bucket} value={bucket} sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>
                  <Checkbox checked={selectedPercentileBuckets.indexOf(bucket) > -1} />
                  <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: percentileBucketColors[bucket],
                      }}
                    />
                    <ListItemText primary={percentileBucketLabels[bucket]} />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Exposure Range Multi-Select */}
        {isCurrentModeExposure && (
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>טווחי {getExposureTitleByColorMode(colorMode)}</InputLabel>
            <Select
              multiple
              value={getSelectedRangesByColorMode(colorMode)}
              onChange={(e) => {
                const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                setSelectedRangesByColorMode(colorMode, value);
              }}
              input={<OutlinedInput label={`טווחי ${getExposureTitleByColorMode(colorMode)}`} />}
              renderValue={(selected) =>
                selected.length === 0 ? 'הכל' : `${selected.length} נבחרו`
              }
              MenuProps={{ sx: { '& .MuiMenu-paper': { direction: 'rtl' } } }}
            >
              {getExposureRangesByColorMode(colorMode).map((range) => (
                <MenuItem key={range} value={range} sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>
                  <Checkbox checked={getSelectedRangesByColorMode(colorMode).indexOf(range) > -1} />
                  <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: getExposureColorsByColorMode(colorMode)[range],
                      }}
                    />
                    <ListItemText primary={`${range}%`} />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        </Box>

        {/* כפתורי Toggle בין תרשים לטבלה - בצד שמאל */}
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_e, newVal) => newVal && setViewMode(newVal as "chart" | "table")}
          sx={{
            '& .MuiToggleButton-root': {
              px: 1.5,
              py: 1,
              border: '1.5px solid rgba(0, 0, 0, 0.12)',
              borderRadius: '8px',
              margin: '0 2px',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                borderColor: 'rgba(25, 118, 210, 0.5)',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(25, 118, 210, 0.12)',
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.16)',
                }
              }
            }
          }}
        >
          <ToggleButton value="chart" aria-label="תרשים">
            <ShowChartIcon />
          </ToggleButton>
          <ToggleButton value="table" aria-label="טבלה">
            <TableChartIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <ToggleButtonGroup
        ref={yieldButtonsRef}
        value={effectiveYieldField}
        exclusive
        onChange={(_e, newVal) => newVal && actualOnYieldFieldChange(newVal)}
        sx={{
          '& .MuiToggleButton-root': {
            px: 2,
            py: 1,
            fontSize: '0.875rem',
            fontWeight: 500,
            border: '1.5px solid rgba(0, 0, 0, 0.12)',
            borderRadius: '8px',
            margin: '0 2px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
              borderColor: 'rgba(25, 118, 210, 0.5)',
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(25, 118, 210, 0.12)',
              borderColor: '#1976d2',
              color: '#1976d2',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.16)',
              }
            }
          }
        }}
      >
        <ToggleButton value="year_to_date_yield">YTD</ToggleButton>
        <ToggleButton value="cumulative_6m_yield">6M</ToggleButton>
        <ToggleButton value="cumulative_12m_yield">1Y</ToggleButton>
        <ToggleButton value="avg_annual_yield_trailing_3yrs">ממוצע 3 שנים</ToggleButton>
        <ToggleButton value="avg_annual_yield_trailing_5yrs">ממוצע 5 שנים</ToggleButton>
        <ToggleButton
          value="yield_trailing_3_yrs"
          sx={{ marginLeft: '16px !important' }}
        >
          מצטברת 3 שנים
        </ToggleButton>
        <ToggleButton value="yield_trailing_5_yrs">מצטברת 5 שנים</ToggleButton>
      </ToggleButtonGroup>

      {/* הצגת chips של פילטרים פעילים */}
      {useFilters && filters && (
        <>
          <ActiveFiltersChips
            filters={filters}
            onRemoveFilter={handleRemoveFilter}
          />

          {/* שורת בקרת תצוגה */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2, flexWrap: 'wrap' }}>
            {/* מספר יצרנים: אם יש פילטר פעיל - כמה יצרנים עונים לפילטר מתוך כלל היצרנים המוצגים בגרף, אחרת סך היצרנים */}
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {`יצרנים ${visibleManufacturersCount} `}
              {filteredFundIds.length > 0 && filteredFundIds.length < allFundsCount && (
                <Box component="span" sx={{ fontWeight: 700 }}>
                  ({filteredManufacturersCount})
                </Box>
              )}
            </Typography>

            {/* כלל האוכלוסייה */}
            <Button
              variant="text"
              size="small"
              onClick={() => setDisplayOptions(prev => ({ ...prev, showAllPopulation: !prev.showAllPopulation }))}
              startIcon={displayOptions.showAllPopulation ? <VisibilityIcon sx={{ml: 1}} /> : <VisibilityOffIcon sx={{ml: 1}}/>}
              sx={{
                color: displayOptions.showAllPopulation ? 'text.primary' : 'text.disabled',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              כלל הקרנות ({allFundsCount})
            </Button>

            {/* קרנות מסוננות */}
            {filteredFundIds.length > 0 && filteredFundIds.length < allFundsCount && (
              <Button
                variant="text"
                size="small"
                onClick={() => setDisplayOptions(prev => ({ ...prev, showFiltered: !prev.showFiltered }))}
                startIcon={displayOptions.showFiltered ? <VisibilityIcon sx={{ml: 1}} /> : <VisibilityOffIcon sx={{ml: 1}} />}
                sx={{
                  color: displayOptions.showFiltered ? 'text.primary' : 'text.disabled',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                קרנות בפילטר ({filteredFundIds.length})
              </Button>
            )}

            {/* קרנות בתיק */}
            {highlightedFunds && highlightedFunds.length > 0 && (
              <Button
                variant="text"
                size="small"
                onClick={() => setDisplayOptions(prev => ({ ...prev, showHighlighted: !prev.showHighlighted }))}
                startIcon={displayOptions.showHighlighted ? <VisibilityIcon sx={{ml: 1}} /> : <VisibilityOffIcon sx={{ml: 1}} />}
                sx={{
                  color: displayOptions.showHighlighted ? 'text.primary' : 'text.disabled',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                קרנות בתיק ({highlightedFunds.length})
              </Button>
            )}

            {/* קרנות נבחרות */}
            {actualSelectedFundIds && actualSelectedFundIds.length > 0 && (
              <Button
                variant="text"
                size="small"
                onClick={() => setDisplayOptions(prev => ({ ...prev, showSelected: !prev.showSelected }))}
                startIcon={displayOptions.showSelected ? <VisibilityIcon sx={{ml: 1}} /> : <VisibilityOffIcon sx={{ml: 1}} />}
                sx={{
                  color: displayOptions.showSelected ? 'text.primary' : 'text.disabled',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                קרנות נבחרות ({actualSelectedFundIds.length})
              </Button>
            )}
          </Box>
        </>
      )}

      {/* תצוגת תרשים או טבלה */}
      {viewMode === "chart" ? (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: fundListPosition === "right" ? "row-reverse" : "column",
              gap: 2,
              alignItems: "flex-start",
              width: "100%",
              maxWidth: "100%",
            }}
          >
            <Box
              height={400}
              sx={{
                flex: fundListPosition === "right" ? "0 0 66.666%" : "1 1 auto",
                minWidth: 0,
                maxWidth: fundListPosition === "right" ? "66.666%" : "100%",
                width: fundListPosition === "right" ? undefined : "100%",
                direction: "ltr",
              }}
            >
              <Scatter ref={chartRef} data={chartData} options={chartOptions} />
            </Box>

            {(actualSelectedFundIds.length > 0 || combinedHighlightedFundIds.length > 0) && (
              <Box
                sx={{
                  flex: fundListPosition === "right" ? "0 0 33.333%" : "1 1 auto",
                  minWidth: 0,
                  maxWidth: fundListPosition === "right" ? "33.333%" : "100%",
                  width: fundListPosition === "right" ? undefined : "100%",
                }}
              >
                {/* קרנות בתיק (highlighted) */}
                <FundAccordionList
                  title="קרנות קיימות בתיק:"
                  fundIds={combinedHighlightedFundIds}
                  funds={funds}
                  borderColor="rgba(255, 152, 0, 1)"
                  isVisible={displayOptions.showHighlighted}
                  onToggleVisibility={() => setDisplayOptions(prev => ({ ...prev, showHighlighted: !prev.showHighlighted }))}
                  selectedFundIds={actualSelectedFundIds}
                  onRemoveFund={(id) => actualOnSelectedFundsChange(actualSelectedFundIds.filter((x) => x !== id))}
                  fundAmountMap={fundAmountMap}
                />

                {/* קרנות שנבחרו (selected but not highlighted) */}
                <FundAccordionList
                  title="קרנות שנבחרו:"
                  fundIds={actualSelectedFundIds.filter(id => !combinedHighlightedFundIds.includes(id))}
                  funds={funds}
                  borderColor="rgba(236, 72, 153, 1)"
                  isVisible={displayOptions.showSelected}
                  onToggleVisibility={() => setDisplayOptions(prev => ({ ...prev, showSelected: !prev.showSelected }))}
                  selectedFundIds={actualSelectedFundIds}
                  onRemoveFund={(id) => actualOnSelectedFundsChange(actualSelectedFundIds.filter((x) => x !== id))}
                />

                {/* כפתור החלפה - רק במצב edit, כאשר יש קרן אחת נבחרת וקרן אחת בתיק */}
                {editMode && onFundSelected && (() => {
                  const selectedNotInPortfolio = actualSelectedFundIds.filter(id => !combinedHighlightedFundIds.includes(id));
                  const canReplace = selectedNotInPortfolio.length === 1 && combinedHighlightedFundIds.length === 1;
                  if (!canReplace) return null;

                  const newFundId = selectedNotInPortfolio[0];
                  const newFund = funds.find(f => f.fund_id === newFundId);
                  if (!newFund) return null;

                  return (
                    <Box mt={1} display="flex" justifyContent="center">
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SwapHorizIcon />}
                        onClick={() => onFundSelected(newFund)}
                      >
                        החלף מסלול
                      </Button>
                    </Box>
                  );
                })()}

              </Box>
            )}
          </Box>

          {(actualSelectedFundIds.length > 0 || combinedHighlightedFundIds.length > 0) && (
            <Box mt={1} width="100%">
              <ProductFundYieldsChart
                fundPathCodes={actualSelectedFundIds}
                defaultColorBy="fund_id"
                defaultMonthsBack={11} />
            </Box>
          )}
        </>
      ) : (
        <Suspense
          fallback={
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 400,
              }}
            >
              <CircularProgress />
            </Box>
          }
        >
          <FundsDataTable
            funds={funds}
            filteredFundIds={filteredFundIds}
            selectedFundIds={actualSelectedFundIds}
            highlightedFunds={highlightedFunds}
            yieldField={effectiveYieldField}
            xAxisField={xAxisField}
            colorMode={colorMode}
            useFilters={useFilters}
            onRowClick={(fundId) => {
              const newSelectedIds = actualSelectedFundIds.includes(fundId)
                ? actualSelectedFundIds.filter((x) => x !== fundId)
                : [...actualSelectedFundIds, fundId];
              actualOnSelectedFundsChange(newSelectedIds);
            }}
          />
        </Suspense>
      )}
    </Box>
  );
};

export default ScatterFundYieldChart;